/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Button, Flash, FormControl, TextInput} from '@primer/react'
import type {ReactElement, ReactNode, RefObject} from 'react'
import {useRef, useState} from 'react'

import {BUTTON_LABELS, LABELS} from '../constants'
import type {EditableShortcutField} from '../hooks/use-shortcut-editor'
import type {UserShortcut} from '../types'
import {isValidShortcutName, isValidShortcutQuery} from '../utils/shortcut'
import {IconAndColorPicker} from './IconAndColorPicker'
import styles from './ShortcutEditingHeader.module.css'

interface ShortcutEditingHeaderProps {
  /**
   * Render function that is expected to return a Filter component (or wrapper like ReposFilter).
   * The function receives an inputRef and className to pass to the Filter.
   *
   * Note: `'width-full'` is passed in automatically via the `className` prop
   * so that the Filter always takes the full width of its container.
   * This behavior can be overridden by passing a different `className` yourself.
   *
   * @example
   * ```tsx
   * renderFilter={filterProps => (
   *   <ReposFilter {...filterProps} {...props}/>
   * )}
   * ```
   */
  renderFilter: (filterProps: {inputRef: RefObject<HTMLInputElement | null>; className: string}) => ReactElement
  onDismiss: () => void
  onSave: () => void
  shortcut: UserShortcut
  setShortcutValue: (fieldName: EditableShortcutField, value: string) => void
  isSaving?: boolean
  errorMessage?: string
  /** Rendered at the end of the icon and title row, after the title field. */
  trailingField?: ReactNode
  /** Rendered between the filter and Cancel. */
  leadingActions?: ReactNode
}

/**
 * Editing header component for creating or editing a saved view.
 *
 * All props can be passed in directly from the `useShortcutEditor` hook, apart from `renderFilter`, which is a
 * render prop for rendering a Filter component specific to the page in which this component is used, and the
 * optional `trailingField` and `leadingActions` slots.
 *
 * The filter component is intended to be shared between this component and the page's default filter UI,
 * to keep the query state consistent between the shortcut editor being open or closed.
 */
export function ShortcutEditingHeader({
  renderFilter,
  onDismiss,
  onSave,
  shortcut,
  setShortcutValue,
  isSaving = false,
  errorMessage,
  trailingField,
  leadingActions,
}: ShortcutEditingHeaderProps) {
  const titleRef = useRef<HTMLInputElement>(null)
  const filterRef = useRef<HTMLInputElement>(null)

  const [hasInteractedWithTitle, setHasInteractedWithTitle] = useState(false)

  function onSaveButtonClick() {
    if (!shortcut) throw new Error('Editing shortcut is undefined while submitting.')

    if (!isValidShortcutName(shortcut)) {
      setHasInteractedWithTitle(true)
      titleRef.current?.focus()
      return
    }

    if (!isValidShortcutQuery(shortcut)) {
      filterRef.current?.focus()
      return
    }

    onSave()
  }

  return (
    <form className={styles.Form}>
      {errorMessage && <Flash variant="danger">{errorMessage}</Flash>}
      <div className={styles.DetailsRow}>
        <FormControl>
          <FormControl.Label>Icon</FormControl.Label>
          <IconAndColorPicker
            selectedColor={shortcut.color}
            selectedIcon={shortcut.icon}
            onApply={(color, icon) => {
              setShortcutValue('color', color)
              setShortcutValue('icon', icon)
            }}
          />
        </FormControl>

        <FormControl required className={styles.TitleFormControl}>
          <FormControl.Label>Title</FormControl.Label>
          <TextInput
            autoFocus
            ref={titleRef}
            block
            placeholder={LABELS.views.defaultName}
            value={shortcut.name}
            onChange={({target}) => {
              setHasInteractedWithTitle(true)
              setShortcutValue('name', target.value)
            }}
          />
          {!isValidShortcutName(shortcut) && hasInteractedWithTitle && (
            <FormControl.Validation variant="error">Title can not be empty</FormControl.Validation>
          )}
        </FormControl>

        {trailingField}
      </div>

      <FormControl>
        <FormControl.Label>Description</FormControl.Label>
        <TextInput
          block
          placeholder="Add a description"
          value={shortcut.description}
          onChange={({target}) => setShortcutValue('description', target.value)}
        />
      </FormControl>

      <hr className={styles.Divider} />

      <div className={styles.FilterRow}>
        <FormControl required className={styles.FilterFormControl}>
          <FormControl.Label visuallyHidden>Query</FormControl.Label>
          {renderFilter({inputRef: filterRef, className: 'width-full'})}
          {!isValidShortcutQuery(shortcut) && (
            <FormControl.Validation variant="error">Query can not be empty</FormControl.Validation>
          )}
        </FormControl>
        <div className={styles.ActionButtons}>
          {leadingActions}
          <Button
            type="button"
            onClick={isSaving ? undefined : onDismiss}
            inactive={isSaving}
            aria-disabled={isSaving || undefined}
          >
            {BUTTON_LABELS.cancel}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={onSaveButtonClick}
            loading={isSaving}
            loadingAnnouncement={LABELS.views.saving}
          >
            {BUTTON_LABELS.save}
          </Button>
        </div>
      </div>
    </form>
  )
}
